import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { buildItemUrl } from "../utils/itemUrl";
import usePageTitle from "../hooks/usePageTitle";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  useMediaQuery,
  useTheme,
  Avatar,
  Chip,
  Link as MuiLink,
} from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Line } from "react-chartjs-2";
import "chart.js/auto";
import dayjs from "dayjs";
import { api, getAuth } from "../api";
import { formatPrice, getPriceDollars } from "../utils/price";
import PaginationControls from "./PaginationControls";
import PriceConfidenceRibbon from "./PriceConfidenceRibbon";
import { toDisplayImageUrl } from "../utils/imageUrl";
import { usePriceVisibility } from "../contexts/PriceVisibilityContext";

const normalizeConfidence = (value) => {
  const v = String(value ?? "").trim().toLowerCase();

  if (
    [
      "high",
      "h",
      "strong",
      "high_active_exact",
      "high_active",
      "high_confidence",
    ].includes(v)
  ) {
    return "high";
  }

  if (
    [
      "medium",
      "med",
      "mid",
      "m",
      "medium_active_exact",
      "medium_active",
      "manual_sold_comp",
      "verified_manual_comp",
      "carry_forward_verified",
      "stale_carry_forward",
    ].includes(v)
  ) {
    return "medium";
  }

  if (
    [
      "low",
      "l",
      "weak",
      "low_active_adjusted",
      "low_active",
      "carry_forward",
      "carry_forward_recent",
      "carry_forward_infra_safe",
      "estimate_low",
      "category_median_fallback",
      "category_only_median_fallback",
      "item_type_median_fallback",
      "multi_source_weighted",
    ].includes(v)
  ) {
    return "low";
  }

  return "";
};

const withEbaySoldCompleted = (url) => {
  if (!url) return "";
  try {
    const parsed = new URL(url);
    if (!parsed.hostname.includes("ebay.")) return url;
    parsed.searchParams.set("LH_Sold", "1");
    parsed.searchParams.set("LH_Complete", "1");
    return parsed.toString();
  } catch {
    return url;
  }
};

function formatDate(dateString) {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (!Number.isFinite(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const normalizeSubcategoryId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const buildSubcategoryTree = (items = []) => {
  const nodes = items
    .map((item) => ({
      ...item,
      subcategory_id: normalizeSubcategoryId(item.subcategory_id),
      parent_subcategory_id: normalizeSubcategoryId(item.parent_subcategory_id),
      children: [],
    }))
    .filter((item) => item.subcategory_id != null);
  const byId = new Map(nodes.map((node) => [node.subcategory_id, node]));
  const roots = [];

  nodes.forEach((node) => {
    const parentId = node.parent_subcategory_id;
    if (parentId != null && byId.has(parentId)) {
      byId.get(parentId).children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNodes = (list) => {
    list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    list.forEach((node) => {
      if (node.children.length > 0) {
        sortNodes(node.children);
      }
    });
  };

  sortNodes(roots);
  return roots;
};

const getSubcategoryScopeIds = (items, selectedValue) => {
  const selectedId = normalizeSubcategoryId(selectedValue);
  if (selectedId == null) return null;
  const normalized = items
    .map((item) => ({
      ...item,
      subcategory_id: normalizeSubcategoryId(item.subcategory_id),
      parent_subcategory_id: normalizeSubcategoryId(item.parent_subcategory_id),
    }))
    .filter((item) => item.subcategory_id != null);
  const byId = new Map(normalized.map((item) => [item.subcategory_id, item]));
  const selected = byId.get(selectedId);
  if (!selected) return null;
  if (selected.parent_subcategory_id != null) {
    return [selectedId];
  }
  const childIds = normalized
    .filter((item) => item.parent_subcategory_id === selectedId)
    .map((item) => item.subcategory_id);
  return [selectedId, ...childIds];
};

const renderSubcategoryChips = (item = {}) => {
  const parent = item.parent_subcategory_name;
  const child = item.subcategory_name;

  const chips = [];

  if (parent) {
    chips.push(
      <Chip
        key="parent"
        label={parent}
        size="small"
        variant="outlined"
        sx={{
          fontWeight: 600,
          height: 24,
          borderRadius: "999px",
          bgcolor: "#fff",
          color: "#111",
          border: "1px solid rgba(255,255,255,0.88)",
          borderColor: "rgba(255,255,255,0.88)",
          boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
        }}
      />
    );
  }

  if (child && child !== parent) {
    chips.push(
      <Chip
        key="child"
        label={child}
        size="small"
        variant="outlined"
        sx={{
          fontWeight: 600,
          height: 24,
          borderRadius: "999px",
          bgcolor: "#fff",
          color: "#111",
          border: "1px solid rgba(255,255,255,0.88)",
          borderColor: "rgba(255,255,255,0.88)",
          boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        gap: 0.75,
        flexWrap: "wrap",
        mt: 0.5,
      }}
    >
      {chips}
    </Box>
  );
};

const getChartBucketConfig = (chartRange, labels = []) => {
  const firstDate = labels.length ? dayjs(labels[0]) : null;
  const lastDate = labels.length ? dayjs(labels[labels.length - 1]) : null;
  const spanMonths =
    firstDate && lastDate && firstDate.isValid() && lastDate.isValid()
      ? Math.max(0, lastDate.diff(firstDate, "month", true))
      : 0;

  switch (chartRange) {
    case "1m":
      return { type: "day", size: 1 };
    case "3m":
      return { type: "day", size: 7 };
    case "6m":
      return { type: "day", size: 14 };
    case "1yr":
      return { type: "month", size: 1 };
    default:
      if (spanMonths <= 3) return { type: "day", size: 7 };
      if (spanMonths <= 8) return { type: "day", size: 14 };
      return { type: "month", size: 1 };
  }
};

const buildDisplayChartSeries = (labels, values, chartRange, mode) => {
  if (!Array.isArray(labels) || !Array.isArray(values) || labels.length !== values.length) {
    return { labels: [], data: [] };
  }
  if (labels.length <= 1) {
    return { labels: [...labels], data: [...values] };
  }

  const bucketConfig = getChartBucketConfig(chartRange, labels);
  if (bucketConfig.type === "day" && bucketConfig.size <= 1) {
    return { labels: [...labels], data: [...values] };
  }

  const parsedDates = labels.map((label) => dayjs(label));
  const firstValidDate = parsedDates.find((date) => date.isValid());
  if (!firstValidDate) {
    return { labels: [...labels], data: [...values] };
  }

  const reducedLabels = [];
  const reducedData = [];
  let activeBucketKey = null;
  let bucketLabel = null;
  let bucketValue = mode === "count" ? 0 : null;

  const flushBucket = () => {
    if (bucketLabel == null) return;
    reducedLabels.push(bucketLabel);
    reducedData.push(mode === "count" ? bucketValue : bucketValue ?? 0);
  };

  labels.forEach((label, index) => {
    const value = Number(values[index] ?? 0) || 0;
    const date = parsedDates[index];
    const effectiveDate = date.isValid() ? date : firstValidDate;
    const bucketKey =
      bucketConfig.type === "month"
        ? effectiveDate.startOf("month").format("YYYY-MM")
        : String(Math.floor(Math.max(0, effectiveDate.diff(firstValidDate, "day")) / bucketConfig.size));

    if (activeBucketKey !== null && bucketKey !== activeBucketKey) {
      flushBucket();
      bucketValue = mode === "count" ? 0 : null;
    }

    activeBucketKey = bucketKey;
    bucketLabel = label;
    if (mode === "count") {
      bucketValue += value;
    } else {
      bucketValue = value;
    }
  });

  flushBucket();

  const lastLabel = labels[labels.length - 1];
  const lastValue = Number(values[values.length - 1] ?? 0) || 0;
  if (reducedLabels[reducedLabels.length - 1] !== lastLabel) {
    reducedLabels.push(lastLabel);
    reducedData.push(lastValue);
  } else {
    reducedData[reducedData.length - 1] = mode === "count" ? reducedData[reducedData.length - 1] : lastValue;
  }

  return { labels: reducedLabels, data: reducedData };
};

const formatChartAxisLabel = (label, chartRange, isMobile) => {
  const date = dayjs(label);
  if (!date.isValid()) return label;
  if (chartRange === "1yr" || chartRange === "max") {
    return isMobile ? date.format("MMM 'YY") : date.format("MMM YYYY");
  }
  return isMobile ? date.format("M/D") : date.format("MMM D");
};

const getChartMaxTicks = (chartRange, isMobile) => {
  if (isMobile) {
    if (chartRange === "1m") return 5;
    if (chartRange === "3m") return 6;
    if (chartRange === "6m") return 6;
    return 5;
  }
  if (chartRange === "1m") return 8;
  if (chartRange === "3m") return 8;
  if (chartRange === "6m") return 7;
  return 6;
};

function getRangePerformance(values) {
  const clean = (Array.isArray(values) ? values : []).map(Number).filter(Number.isFinite);
  if (clean.length < 2) {
    return { hasData: false, delta: null, pct: null };
  }

  const first = clean[0];
  const last = clean[clean.length - 1];
  const delta = last - first;
  const pct = first !== 0 ? (delta / first) * 100 : null;

  return { hasData: true, first, last, delta, pct };
}

const formatSignedDollarChange = (value) => {
  if (!Number.isFinite(Number(value))) return "";
  const n = Number(value);
  const sign = n > 0 ? "+" : n < 0 ? "-" : "";
  return `${sign}${Math.abs(n).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  })}`;
};

const formatSignedPercentChange = (value) => {
  if (!Number.isFinite(Number(value))) return "";
  const n = Number(value);
  const sign = n > 0 ? "+" : n < 0 ? "-" : "";
  return `${sign}${Math.abs(n).toFixed(1)}%`;
};

const chartOptions = (title, isMobile, chartRange) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    title: {
      display: true,
      text: title,
      font: { size: isMobile ? 14 : 18, weight: "bold" },
      padding: { top: isMobile ? 6 : 10, bottom: isMobile ? 6 : 10 },
    },
    tooltip: {
      callbacks: {
        title: (items) => {
          const label = items?.[0]?.label;
          return label ? formatChartAxisLabel(label, chartRange, false) : "";
        },
        label: (ctx) => {
          const isItemsChart = ctx.dataset.label === "Items Added";
          return isItemsChart
            ? `${ctx.raw} on ${formatChartAxisLabel(ctx.label, chartRange, false)}`
            : `${formatPrice({ price: Number(ctx.raw).toFixed(2) }) || "TBD"} on ${formatChartAxisLabel(ctx.label, chartRange, false)}`;
        },
      },
    },
    legend: { labels: { font: { size: isMobile ? 10 : 12 } } },
  },
  scales: {
    x: {
      title: {
        display: true,
        text: "Date",
        font: { weight: "bold", size: isMobile ? 10 : 12 },
      },
      ticks: {
        autoSkip: true,
        maxRotation: isMobile ? 0 : 0,
        minRotation: 0,
        font: { size: isMobile ? 7 : 10 },
        maxTicksLimit: getChartMaxTicks(chartRange, isMobile),
        callback: function(value) {
          const label = this.getLabelForValue(value);
          return formatChartAxisLabel(label, chartRange, isMobile);
        },
      },
      grid: {
        display: true,
        color: "rgba(0,0,0,0.1)",
        drawOnChartArea: true,
        drawTicks: false,
      },
    },
    y: {
      title: {
        display: true,
        text: title === "Items Added by Day" ? "# of Items" : "Value ($)",
        font: { weight: "bold", size: isMobile ? 10 : 12 },
      },
      ticks: { font: { size: isMobile ? 8 : 10 } },
      min: 0,
      grid: { color: "rgba(0,0,0,0.1)" },
    },
  },
});

const solidActionSx = {
  fontWeight: 700,
  height: 40,
  minWidth: 92,
};

const ProfitLossOverview = () => {
  const { showPrices, pricesLoaded } = usePriceVisibility();
  usePageTitle("Profit & Loss");
  const navigate = useNavigate();
  const { username: routeUsername } = useParams();

  const [currentUser, setCurrentUser] = useState(null);
  const [viewUsername, setViewUsername] = useState("");

  const [collection, setCollection] = useState([]);
  const [topValues, setTopValues] = useState([]);
  const [topGainers, setTopGainers] = useState([]);
  const [topLosers, setTopLosers] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSubcategory, setSelectedSubcategory] = useState("all");
  const [sortItemOption, setSortItemOption] = useState("high");
  const [currentPage, setCurrentPage] = useState(1);
  const [chartRange, setChartRange] = useState("max");

  const [subcategories, setSubcategories] = useState([]);
  const [priceHistory, setPriceHistory] = useState({ labels: [], data: [] });
  const [addedItemsHistory, setAddedItemsHistory] = useState([]);

  const detailedRef = useRef(null);
  const itemsPerPage = 12;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const subcategoryTree = useMemo(() => buildSubcategoryTree(subcategories), [subcategories]);
  const subcategoryScopeIds = useMemo(
    () => getSubcategoryScopeIds(subcategories, selectedSubcategory),
    [subcategories, selectedSubcategory]
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  // Resolve route user
  useEffect(() => {
    const { user } = getAuth();
    setCurrentUser(user || null);

    if (routeUsername && routeUsername.trim()) {
      setViewUsername(routeUsername.trim());
      return;
    }
    const uname = user?.username || user?.name;
    if (uname) {
      navigate(`/profitloss/${encodeURIComponent(uname)}`, { replace: true });
    } else {
      setViewUsername("");
      return;
    }
  }, [routeUsername, navigate]);

  const buildChartsFromItems = useCallback((items, rangeKey) => {
    const endDate = dayjs().startOf("day");
    const validDates = items
      .map((it) => it.added_on)
      .filter(Boolean)
      .map((d) => dayjs(d).startOf("day"));
    const minDate = validDates.length
      ? validDates.reduce((a, b) => (a.isBefore(b) ? a : b))
      : endDate;

    let startDate;
    switch (rangeKey) {
      case "1m":
        startDate = endDate.subtract(1, "month");
        break;
      case "3m":
        startDate = endDate.subtract(3, "month");
        break;
      case "6m":
        startDate = endDate.subtract(6, "month");
        break;
      case "1yr":
        startDate = endDate.subtract(1, "year");
        break;
      default:
        startDate = minDate;
    }
    if (startDate.isBefore(minDate)) startDate = minDate;

    const labels = [];
    const totals = [];
    const itemsByDay = {};

    let cursor = startDate.clone();
    while (cursor.isBefore(endDate) || cursor.isSame(endDate)) {
      const currentCursor = cursor;
      const key = currentCursor.format("MM/DD/YY");
      labels.push(key);

      const total = items
        .filter((it) => it.added_on && !dayjs(it.added_on).startOf("day").isAfter(currentCursor))
        .reduce((sum, it) => sum + (Number(it.currentValue ?? it.current_value ?? 0) || 0), 0);
      totals.push(total);
      cursor = cursor.add(1, "day");
    }

    items.forEach((it) => {
      if (!it.added_on) return;
      const d = dayjs(it.added_on).startOf("day");
      if (d.isBefore(startDate) || d.isAfter(endDate)) return;
      const key = d.format("MM/DD/YY");
      itemsByDay[key] = (itemsByDay[key] || 0) + 1;
    });

    const addedSeries = labels
      .map((date) => ({ date, count: itemsByDay[date] || 0 }))
      .filter((row) => row.count > 0);

    setPriceHistory({ labels, data: totals });
    setAddedItemsHistory(addedSeries);
  }, []);

  // Fetch collection
  const fetchCollection = useCallback(async (username, rangeKey) => {
    try {
      const { data } = await api.get(
        `/api/mycollection/${encodeURIComponent(username)}`
      );
      const payload = Array.isArray(data) ? data : data?.items || [];
      const items = payload.map((item) => {
        const purchase = Number(item.purchase_price ?? 0) || 0;
        const current = getPriceDollars(item) ?? 0;
        return {
          ...item,
          price: current,
          purchasePrice: purchase,
          currentValue: current,
          profitLoss: current - purchase,
        };
      });
      setCollection(items);
    } catch (err) {
      console.error("❌ Error fetching profit/loss data:", err);
      setCollection([]);
      setTopValues([]);
      setTopGainers([]);
      setTopLosers([]);
      setPriceHistory({ labels: [], data: [] });
      setAddedItemsHistory([]);
    }
  }, []);

  useEffect(() => {
    if (viewUsername) fetchCollection(viewUsername, chartRange);
  }, [viewUsername, chartRange, fetchCollection]);

  // Subcategories
  useEffect(() => {
    if (!viewUsername) return;
    (async () => {
      try {
        const { data } = await api.get(
          `/api/subcategories/user/${encodeURIComponent(viewUsername)}`
        );
        const list = Array.isArray(data) ? data : [];
        setSubcategories(
          list.map((s) => ({
            ...s,
            subcategory_id: normalizeSubcategoryId(s.subcategory_id),
            parent_subcategory_id: normalizeSubcategoryId(s.parent_subcategory_id),
          }))
        );
      } catch (err) {
        console.error("❌ Error fetching subcategories:", err);
        setSubcategories([]);
      }
    })();
  }, [viewUsername]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedSubcategory, sortItemOption]);

  const calculateRankings = (items) => {
    if (!items?.length) {
      setTopValues([]);
      setTopGainers([]);
      setTopLosers([]);
      return;
    }
    setTopValues(
      [...items].sort((a, b) => b.currentValue - a.currentValue).slice(0, 10)
    );
    setTopGainers(
      [...items].sort((a, b) => b.profitLoss - a.profitLoss).slice(0, 10)
    );
    setTopLosers(
      items
      .filter((i) => i.profitLoss < 0)
        .sort((a, b) => a.profitLoss - b.profitLoss)
        .slice(0, 10)
    );
  };

  const scopedCollection = useMemo(() => {
    if (subcategoryScopeIds === null) return collection;
    return collection.filter((it) =>
      subcategoryScopeIds.includes(Number(it.subcategory_id))
    );
  }, [collection, subcategoryScopeIds]);

  useEffect(() => {
    calculateRankings(scopedCollection);
  }, [scopedCollection]);

  useEffect(() => {
    buildChartsFromItems(scopedCollection, chartRange);
  }, [chartRange, scopedCollection, buildChartsFromItems]);

  const getDisplayName = (it) =>
    it.item_name || it.name || "Unnamed Item";

  const renderSubcategoryMenuItems = (nodes = [], depth = 0) =>
    nodes.flatMap((sub) => [
      <MenuItem key={sub.subcategory_id} value={String(sub.subcategory_id)}>
        <Box sx={{ pl: depth * 2 }}>{sub.name}</Box>
      </MenuItem>,
      ...(sub.children?.length
        ? renderSubcategoryMenuItems(sub.children, depth + 1)
        : []),
    ]);

  const filteredCollection = scopedCollection.filter((it) => {
    const matchesSearch = [
      getDisplayName(it),
      it.brand,
      it.category,
      it.subcategory_name,
      it.parent_subcategory_name,
      it.player,
      it.set_name,
      it.edition_variant,
      it.number,
      it.year_released,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const sortedCollection = [...filteredCollection].sort((a, b) => {
    const priceA = Number(a.currentValue) || 0;
    const priceB = Number(b.currentValue) || 0;
    const nameA = getDisplayName(a).toLowerCase();
    const nameB = getDisplayName(b).toLowerCase();
    const dateA = new Date(a.added_on ?? a.date_added ?? a.created_at);
    const dateB = new Date(b.added_on ?? b.date_added ?? b.created_at);
    switch (sortItemOption) {
      case "high":
        return priceB - priceA;
      case "low":
        return priceA - priceB;
      case "az":
        return nameA.localeCompare(nameB);
      case "za":
        return nameB.localeCompare(nameA);
      case "newest":
        return dateB - dateA;
      case "oldest":
        return dateA - dateB;
      default:
        return 0;
    }
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedCollection.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.max(
    1,
    Math.ceil(sortedCollection.length / itemsPerPage)
  );

  const totalPurchase = scopedCollection.reduce(
    (sum, it) => sum + (it.purchasePrice || 0),
    0
  );
  const totalCurrent = scopedCollection.reduce(
    (sum, it) => sum + (it.currentValue || 0),
    0
  );
  const netPL = totalCurrent - totalPurchase;

  const loggedHandle = (
    currentUser?.username || currentUser?.name || ""
  ).toLowerCase();
  const displayMatch =
    loggedHandle &&
    viewUsername &&
    loggedHandle === viewUsername.toLowerCase();

  const avatarSrc =
    displayMatch && currentUser?.profile_picture
      ? currentUser.profile_picture
      : "/logos/default-avatar.jpg";

  const displayPriceHistory = buildDisplayChartSeries(
    priceHistory.labels,
    priceHistory.data,
    chartRange,
    "value"
  );
  const displayAddedItemsHistory = buildDisplayChartSeries(
    addedItemsHistory.map((p) => p.date),
    addedItemsHistory.map((p) => p.count),
    chartRange,
    "count"
  );
  const valuePointRadius =
    displayPriceHistory.data.length > 24 ? 0 : displayPriceHistory.data.length > 12 ? 1.5 : 2.5;
  const itemsPointRadius =
    displayAddedItemsHistory.data.length > 24 ? 0 : displayAddedItemsHistory.data.length > 12 ? 1.5 : 2.5;

  const selectedRangeValuePerformance = getRangePerformance(displayPriceHistory.data);
  const selectedRangeItemsAdded = displayAddedItemsHistory.data.reduce(
    (sum, value) => sum + (Number(value) || 0),
    0
  );
  const selectedRangeValueColor = !selectedRangeValuePerformance.hasData
    ? "text.secondary"
    : selectedRangeValuePerformance.delta > 0
    ? "success.main"
    : selectedRangeValuePerformance.delta < 0
    ? "error.main"
    : "text.secondary";
  const selectedRangeValueText = selectedRangeValuePerformance.hasData
    ? `Selected Range: ${formatSignedDollarChange(selectedRangeValuePerformance.delta)}${
        selectedRangeValuePerformance.pct != null
          ? ` (${formatSignedPercentChange(selectedRangeValuePerformance.pct)})`
          : ""
      }`
    : "Selected Range: Not enough data";

  const valueChartData = {
    labels: displayPriceHistory.labels,
    datasets: [
      {
        label: "Collection Value",
        data: displayPriceHistory.data,
        borderColor: "rgba(54, 162, 235, 1)",
        backgroundColor: "rgba(54, 162, 235, 0.14)",
        borderWidth: 2.5,
        fill: true,
        tension: 0.22,
        pointRadius: valuePointRadius,
        pointHoverRadius: valuePointRadius > 0 ? 4 : 3,
        pointHitRadius: 10,
        pointBackgroundColor: "rgba(54, 162, 235, 0.95)",
        pointBorderWidth: 0,
        spanGaps: true,
      },
    ],
  };

  const itemsChartData = {
    labels: displayAddedItemsHistory.labels,
    datasets: [
      {
        label: "Items Added",
        data: displayAddedItemsHistory.data,
        borderColor: "rgba(75, 192, 192, 1)",
        backgroundColor: "rgba(75, 192, 192, 0.14)",
        borderWidth: 2.5,
        fill: true,
        tension: 0.18,
        pointRadius: itemsPointRadius,
        pointHoverRadius: itemsPointRadius > 0 ? 4 : 3,
        pointHitRadius: 10,
        pointBackgroundColor: "rgba(75, 192, 192, 0.95)",
        pointBorderWidth: 0,
        spanGaps: true,
      },
    ],
  };

  if (!routeUsername && !(currentUser?.username || currentUser?.name)) {
    return (
      <Box
        sx={{
          minHeight: "50vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 2,
          textAlign: "center",
        }}
      >
        <Typography variant="h6">Please log in to view your Profit/Loss.</Typography>
        <Button variant="contained" onClick={() => navigate("/login")}>
          Go to Login
        </Button>
      </Box>
    );
  }
  if (!pricesLoaded) return <Box sx={{ p: 4, textAlign: "center" }}><Typography>Loading price display settings...</Typography></Box>;
  if (!showPrices) return null;

  return (
    <Box
      className="main-content"
      sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 }, maxWidth: 1200, mx: "auto" }}
      ref={detailedRef}
    >
      {/* Header */}
      <Paper
        sx={{
          p: { xs: 2, md: 2.5 },
          mb: 2,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          justifyContent: "space-between",
          gap: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            src={avatarSrc}
            sx={{ width: 100, height: 100 }}
            imgProps={{
              referrerPolicy: "no-referrer",
              onError: (e) => {
                e.currentTarget.src = "/logos/default-avatar.jpg";
              },
            }}
          />
          <Typography variant="h6" sx={{ mb: 1 }}>
            @{viewUsername}&apos;s Profit &amp; Loss
          </Typography>
        </Box>

        <Box sx={{ textAlign: { xs: "left", md: "center" } }}>
          <Typography variant="body2">
            <strong>Total Items:</strong> {scopedCollection.length}
          </Typography>
          <Typography variant="body2">
            <strong>Total Purchase:</strong>{" "}
            {totalPurchase.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}
          </Typography>
          <Typography variant="body2">
            <strong>Current Value:</strong>{" "}
            {totalCurrent.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}
          </Typography>
          <Typography
            variant="body2"
            sx={{ mt: 0.5, color: netPL >= 0 ? "primary.light" : "error.main" }}
          >
            <strong>Net P/L:</strong>{" "}
            {netPL.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}
          </Typography>
        </Box>

      </Paper>

      {/* Filters near detailed overview */}
      <Paper
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          width: "100%",
          flexWrap: { xs: "wrap", md: "nowrap" },
        }}
      >
        <Box
          sx={{
            flex: { xs: "1 1 100%", md: "1 1 auto" },
            minWidth: { xs: "100%", md: 260 },
          }}
        >
          <input
            type="text"
            placeholder="Search Collection..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: "16.5px 14px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              fontSize: "1rem",
              width: "100%",
              height: "56px",
              boxSizing: "border-box",
            }}
          />
        </Box>

        <FormControl
          sx={{
            width: { xs: "100%", md: 200 },
            flex: "0 0 auto",
          }}
        >
          <InputLabel id="subcat-label">Subcategory</InputLabel>
          <Select
            labelId="subcat-label"
            value={selectedSubcategory}
            label="Subcategory"
            onChange={(e) => {
              const v = e.target.value;
              setSelectedSubcategory(v === "all" ? "all" : String(v));
            }}
            MenuProps={{ disableScrollLock: true }}
          >
            <MenuItem value="all">Subcategory (All)</MenuItem>
            {renderSubcategoryMenuItems(subcategoryTree)}
          </Select>
        </FormControl>

        <FormControl
          sx={{
            width: { xs: "100%", md: 200 },
            flex: "0 0 auto",
          }}
        >
          <InputLabel>Sort Items</InputLabel>
          <Select
            value={sortItemOption}
            label="Sort Items"
            onChange={(e) => setSortItemOption(e.target.value)}
            MenuProps={{ disableScrollLock: true }}
          >
            <MenuItem value="high">Highest Value</MenuItem>
            <MenuItem value="low">Lowest Value</MenuItem>
            <MenuItem value="az">Name A–Z</MenuItem>
            <MenuItem value="za">Name Z–A</MenuItem>
            <MenuItem value="newest">Newest</MenuItem>
            <MenuItem value="oldest">Oldest</MenuItem>
          </Select>
        </FormControl>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "nowrap",
            flex: "0 0 auto",
            justifyContent: "flex-end",
            width: { xs: "100%", md: "auto" },
          }}
        >
          {["1m", "3m", "6m", "1yr", "max"].map((r) => (
            <Button
              key={r}
              variant={chartRange === r ? "contained" : "outlined"}
              onClick={() => setChartRange(r)}
              size="small"
              sx={{ minWidth: 52, px: 1.5, borderRadius: 1, fontWeight: 700 }}
            >
              {r.toUpperCase()}
            </Button>
          ))}
        </Box>

        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "center",
            mt: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: selectedRangeValueColor,
            }}
          >
            {selectedRangeValueText}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: "text.secondary",
            }}
          >
            Items Added: {selectedRangeItemsAdded}
          </Typography>
        </Box>
      </Paper>

      {/* Charts */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: { xs: 1.5, sm: 2 }, height: { xs: 250, sm: 280, md: 340 } }}>
            {priceHistory.labels.length ? (
              <Line
                data={valueChartData}
                options={chartOptions("Collection Value Over Time", isMobile, chartRange)}
              />
            ) : (
              <Typography>No price history available yet.</Typography>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: { xs: 1.5, sm: 2 }, height: { xs: 250, sm: 280, md: 340 } }}>
            {addedItemsHistory.length ? (
              <Line
                data={itemsChartData}
                options={chartOptions("Items Added by Day", isMobile, chartRange)}
              />
            ) : (
              <Typography>No items-added history yet.</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Top lists */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Top Values */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Top Values
            </Typography>
            {topValues.length ? (
              <Table size="small">
                <TableBody>
                  {topValues.map((it, idx) => (
                    <TableRow key={idx}>
                      <TableCell sx={{ width: 30 }}>{idx + 1}</TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Avatar
                            src={toDisplayImageUrl(it.image_url) || "/logos/default-item.jpg"}
                            sx={{ width: 32, height: 32 }}
                            variant="rounded"
                            imgProps={{
                              referrerPolicy: "no-referrer",
                              onError: (e) => {
                                e.currentTarget.src =
                                  "/logos/default-item.jpg";
                              },
                            }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Link to={buildItemUrl(it)}>
                              {getDisplayName(it)}
                            </Link>
                            {renderSubcategoryChips(it)}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {it.currentValue.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2">
                Not enough data to show top values.
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Top Gainers */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Top Gainers
            </Typography>
            {topGainers.length ? (
              <Table size="small">
                <TableBody>
                  {topGainers.map((it, idx) => (
                    <TableRow key={idx}>
                      <TableCell sx={{ width: 30 }}>{idx + 1}</TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Avatar
                            src={toDisplayImageUrl(it.image_url) || "/logos/default-item.jpg"}
                            sx={{ width: 32, height: 32 }}
                            variant="rounded"
                            imgProps={{
                              referrerPolicy: "no-referrer",
                              onError: (e) => {
                                e.currentTarget.src =
                                  "/logos/default-item.jpg";
                              },
                            }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Link to={buildItemUrl(it)}>
                              {getDisplayName(it)}
                            </Link>
                            {renderSubcategoryChips(it)}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {it.profitLoss.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2">
                Not enough data to show top gainers.
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Top Losers */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Top Losers
            </Typography>
            {topLosers.length ? (
              <Table size="small">
                <TableBody>
                  {topLosers.map((it, idx) => (
                    <TableRow key={idx}>
                      <TableCell sx={{ width: 30 }}>{idx + 1}</TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Avatar
                            src={toDisplayImageUrl(it.image_url) || "/logos/default-item.jpg"}
                            sx={{ width: 32, height: 32 }}
                            variant="rounded"
                            imgProps={{
                              referrerPolicy: "no-referrer",
                              onError: (e) => {
                                e.currentTarget.src =
                                  "/logos/default-item.jpg";
                              },
                            }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Link to={buildItemUrl(it)}>
                              {getDisplayName(it)}
                            </Link>
                            {renderSubcategoryChips(it)}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {it.profitLoss.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2">
                Not enough data to show top losers.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Detailed items table */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Detailed Profit / Loss
        </Typography>

        {currentItems.length === 0 ? (
          <Typography>No items match the current filters.</Typography>
        ) : (
          <>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size={isMobile ? "small" : "medium"}>
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>Purchase Price</TableCell>
                    <TableCell>Current Value</TableCell>
                    <TableCell>Profit / Loss</TableCell>
                    <TableCell>Added On</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentItems.map((it, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Avatar
                            src={toDisplayImageUrl(it.image_url) || "/logos/default-item.jpg"}
                            sx={{ width: 32, height: 32 }}
                            variant="rounded"
                            imgProps={{
                              referrerPolicy: "no-referrer",
                              onError: (e) => {
                                e.currentTarget.src =
                                  "/logos/default-item.jpg";
                              },
                            }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Link to={buildItemUrl(it)}>
                              {getDisplayName(it)}
                            </Link>
                            {renderSubcategoryChips(it)}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {it.purchasePrice.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                          <Typography variant="body2">
                            {it.currentValue.toLocaleString("en-US", {
                              style: "currency",
                              currency: "USD",
                            })}
                          </Typography>
                          {(() => {
                            const priceConfidence = normalizeConfidence(
                              it.price_confidence ||
                                it.pricing_confidence ||
                                it.confidence ||
                                it.priceConfidence ||
                                it.pricingConfidence
                            );
                            const priceLastUpdated = formatDate(it.price_last_updated);
                            const compsUrl = it.price_comps_url || "";
                            const compsHref = withEbaySoldCompleted(compsUrl);
                            const showMeta = Boolean(priceLastUpdated || priceConfidence || compsHref);
                            if (!showMeta) return null;
                            return (
                              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.75 }}>
                                {priceConfidence && (
                                  <PriceConfidenceRibbon confidence={priceConfidence} />
                                )}
                                {priceLastUpdated && (
                                  <Typography variant="caption" color="text.secondary">
                                    Price last updated: {priceLastUpdated}
                                  </Typography>
                                )}
                                {compsHref && (
                                  <MuiLink
                                    href={compsHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    underline="hover"
                                    sx={{ fontSize: "0.75rem" }}
                                  >
                                    View sold comps on eBay
                                  </MuiLink>
                                )}
                              </Box>
                            );
                          })()}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          color:
                            it.profitLoss > 0
                              ? "green"
                              : it.profitLoss < 0
                              ? "error.main"
                              : "inherit",
                        }}
                      >
                        {it.profitLoss.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </TableCell>
                      <TableCell>
                        {it.added_on
                          ? new Date(it.added_on).toLocaleDateString()
                          : it.created_at
                          ? new Date(it.created_at).toLocaleDateString()
                          : "Unknown"}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          color="primary"
                          disableElevation
                          size="small"
                          onClick={() => navigate(buildItemUrl(it))}
                          sx={solidActionSx}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              sx={{ mt: 2 }}
              buttonSx={solidActionSx}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ProfitLossOverview;
